import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import type { Channel } from '@/api/endpoints/channels'
import { useDisconnectInstagram, type InstagramDestination } from '@/api/endpoints/destinations'
import { startOAuthOrReport } from '@/api/endpoints/oauth'
import { Button } from '@/components/ui/Button'
import { DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { Spinner } from '@/components/ui/Spinner'
import { errorMessage } from '@/lib/errors'
import { displayHandle } from '../../destinations/model'
import { InstagramAccountCard } from './InstagramAccountCard'
import styles from '../../PlatformCard.module.scss'

/**
 * O cartão do Instagram com as chamadas: Conectar/Reconectar sai para o
 * consentimento do Instagram (``POST .../instagram/connect``), e Desconectar
 * pede confirmação antes do ``POST /auth/instagram/disconnect``.
 */
export function InstagramConnection({ channel, destination }: { channel: Channel; destination: InstagramDestination }) {
  const disconnect = useDisconnectInstagram()
  const [confirming, setConfirming] = useState(false)
  const handle = destination.account ? displayHandle(destination.account.username) : ''

  return (
    <>
      <InstagramAccountCard
        destination={destination}
        busy={disconnect.isPending}
        onConnect={() => {
          startOAuthOrReport('instagram', channel.slug, (m) => toast.error(m))
        }}
        onDisconnect={() => {
          setConfirming(true)
        }}
      />
      <DialogRoot
        open={confirming}
        onOpenChange={(next) => {
          if (!next && !disconnect.isPending) setConfirming(false)
        }}
      >
        <DialogContent
          role="alertdialog"
          hideClose
          title={
            <span className={styles.dialogTitle}>
              <AlertTriangle aria-hidden color="var(--warning)" size={18} />
              Desconectar o Instagram?
            </span>
          }
          description={`${handle || 'A conta do Instagram'} deixa de estar ligada ao canal ${channel.name}.`}
          footer={
            <>
              <Button
                variant="secondary"
                disabled={disconnect.isPending}
                onClick={() => {
                  setConfirming(false)
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                leadingIcon={disconnect.isPending ? <Spinner /> : undefined}
                aria-disabled={disconnect.isPending}
                onClick={() => {
                  if (disconnect.isPending) return
                  disconnect.mutate(channel.slug, {
                    onSuccess: () => {
                      setConfirming(false)
                      toast.success(`Instagram desconectado de ${channel.name}`)
                    },
                    onError: (err) => {
                      toast.error(errorMessage(err, 'Falha ao desconectar.'))
                    },
                  })
                }}
              >
                {disconnect.isPending ? 'Desconectando…' : 'Desconectar'}
              </Button>
            </>
          }
        >
          <div className={styles.notes}>
            <ul className={styles.steps}>
              <li>Apaga a credencial que o Prism guarda.</li>
              <li>Desliga o destino e cancela os posts que ainda não saíram.</li>
              <li>Os Reels já publicados continuam no perfil.</li>
            </ul>
            <p className={styles.note}>
              O Instagram não deixa o Prism revogar o acesso por conta própria. Para cortar de vez, remova o app em
              Instagram → Configurações → Apps e sites.
            </p>
          </div>
        </DialogContent>
      </DialogRoot>
    </>
  )
}
